import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useAuthQuery as useQuery } from '../../shared/hooks/useAuthQuery';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import { Input } from '../../catalyst-components/input';
import { Textarea } from '../../catalyst-components/textarea';
import { Button } from '../../catalyst-components/button';
import { Field, Label } from '../../catalyst-components/fieldset';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../../catalyst-components/accordion';
import { MagnifyingGlassIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

import { parseMarkdown } from '../../shared/helpers/markdown';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BaseContext } from '../../shared/base-context';
import { MainRoutes } from '../../shared/models';

const EMPTY_ARRAY: any[] = [];

export const FAQ: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { convexUserId } = useContext(BaseContext);
  const categoriesQuery = useQuery(api.faqs.listActiveCategories);
  const faqsQuery = useQuery(api.faqs.listActiveFAQs);
  const submitFeedback = useMutation(api.feedback.submit);
  const categories = categoriesQuery ?? EMPTY_ARRAY;
  const allFAQs = faqsQuery ?? EMPTY_ARRAY;
  const [searchTerm, setSearchTerm] = useState('');

  // Support form state
  const [supportSubject, setSupportSubject] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportSubmitted, setSupportSubmitted] = useState(false);
  const [supportError, setSupportError] = useState('');

  // Group FAQs by category
  const faqsByCategory = useMemo(() => {
    const grouped: Record<string, typeof allFAQs> = {};
    for (const faq of allFAQs) {
      const catId = faq.category_id;
      if (!grouped[catId]) grouped[catId] = [];
      grouped[catId].push(faq);
    }
    return grouped;
  }, [allFAQs]);

  // Filter based on search
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return categories.map(cat => ({
        category: cat,
        faqs: faqsByCategory[cat._id] || [],
      })).filter(item => item.faqs.length > 0);
    }

    const term = searchTerm.toLowerCase();
    return categories.map(cat => ({
      category: cat,
      faqs: (faqsByCategory[cat._id] || []).filter(faq =>
        faq.question.toLowerCase().includes(term) ||
        faq.answer.toLowerCase().includes(term)
      ),
    })).filter(item => item.faqs.length > 0);
  }, [categories, faqsByCategory, searchTerm]);

  // Handle anchor navigation from URL hash
  useEffect(() => {
    if (location.hash) {
      const anchor = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(anchor);
        element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [location.hash, categories]);

  const handleSupportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supportSubject.trim() || !supportMessage.trim()) {
      setSupportError('Please fill in both fields.');
      return;
    }
    if (!convexUserId) {
      setSupportError('You must be logged in to submit a request.');
      return;
    }
    setSupportSubmitting(true);
    setSupportError('');
    try {
      await submitFeedback({
        user_id: convexUserId as Id<"users">,
        type: 'support_request',
        subject: supportSubject.trim(),
        message: supportMessage.trim(),
      });
      setSupportSubmitted(true);
      setSupportSubject('');
      setSupportMessage('');
    } catch {
      setSupportError('Something went wrong. Please try again.');
    } finally {
      setSupportSubmitting(false);
    }
  };

  const isLoading = categoriesQuery === undefined || faqsQuery === undefined;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background pt-24">
        <div className="max-w-4xl mx-auto px-6 pt-8">
          <Button
            color="light"
            onClick={() => navigate(MainRoutes.Progress)}
            className="mb-4"
          >
            {t('common.backHome')}
          </Button>
          <div className="flex items-center justify-center mt-12">
            <div className="text-center animate-fade-in">
              <img src="/boar_track_v2_a_png.png" alt="Loading..." className="h-12 w-12 mx-auto mb-4" />
              <p className="text-muted-foreground">Loading FAQs...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24">
      {/* Header */}
      <div className="border-b border-border/50 bg-gradient-to-r from-primary/5 to-accent/5 px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <Button
            color="light"
            onClick={() => navigate(MainRoutes.Progress)}
            className="mb-4"
          >
            {t('common.backHome')}
          </Button>
        </div>
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-4">
            <img
              src="/logo.png"
              alt="Gruntled logo"
              className="w-[400px] object-contain"
            />
          </div>
          <h1 className="text-4xl font-semibold text-foreground mb-3">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-muted-foreground mb-8">
            Find answers to common questions about Gruntled
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search FAQs..."
              className="pl-12 pr-10 py-3 text-base"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {filteredData.length === 0 ? (
          <div className="text-center py-12">
            {searchTerm ? (
              <div>
                <p className="text-muted-foreground text-lg mb-2">
                  No FAQs found matching "{searchTerm}"
                </p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-primary hover:underline text-sm"
                >
                  Clear search
                </button>
              </div>
            ) : (
              <p className="text-muted-foreground text-lg">
                No FAQs available yet. Check back soon!
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {filteredData.map(({ category, faqs }) => (
              <div
                key={category._id}
                id={category.name}
                className="bg-card rounded-xl border-2 border-border/50 overflow-hidden shadow-sm"
              >
                <div className="bg-gradient-to-r from-primary/5 to-accent/5 px-6 py-4 border-b border-border/50">
                  <h2 className="text-xl font-semibold text-foreground">
                    {category.display_name}
                  </h2>
                  {category.description && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {category.description}
                    </p>
                  )}
                </div>

                <Accordion type="single" collapsible>
                  {faqs.map((faq) => (
                    <AccordionItem
                      key={faq._id}
                      value={faq._id}
                      className="border-b border-border/30 last:border-b-0"
                    >
                      <AccordionTrigger className="px-6 py-4 hover:bg-muted/20 transition-colors text-left">
                        <span className="font-medium text-foreground">
                          {faq.question}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent value={faq._id}>
                        <div className="px-6 pb-4">
                          <div
                            className="prose prose-sm max-w-none text-muted-foreground leading-relaxed"
                            dangerouslySetInnerHTML={{
                              __html: parseMarkdown(faq.answer),
                            }}
                          />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        )}

        {/* Support Request Form */}
        <div className="mt-12 bg-card rounded-xl border-2 border-border/50 overflow-hidden shadow-sm">
          <div className="bg-gradient-to-r from-primary/5 to-accent/5 px-6 py-4 border-b border-border/50">
            <h3 className="text-xl font-semibold text-foreground">
              Still have questions?
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Send us a support request and we'll get back to you.
            </p>
          </div>
          <div className="p-6">
            {supportSubmitted ? (
              <div className="text-center py-6">
                <CheckCircleIcon className="h-12 w-12 text-primary mx-auto mb-3" />
                <h4 className="text-lg font-semibold text-foreground mb-1">Request sent!</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  We'll get back to you as soon as possible.
                </p>
                <Button
                  color="light"
                  className="rounded-full"
                  onClick={() => setSupportSubmitted(false)}
                >
                  Send another request
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSupportSubmit} className="space-y-4">
                <Field>
                  <Label>Subject</Label>
                  <Input
                    required
                    placeholder="What do you need help with?"
                    value={supportSubject}
                    onChange={(e) => setSupportSubject(e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>Message</Label>
                  <Textarea
                    required
                    rows={4}
                    placeholder="Tell us more about your question or issue..."
                    value={supportMessage}
                    onChange={(e) => setSupportMessage(e.target.value)}
                  />
                </Field>
                {supportError && (
                  <p className="text-sm text-destructive">{supportError}</p>
                )}
                <div className="flex justify-end">
                  <Button
                    color="primary"
                    type="submit"
                    className="rounded-full"
                    disabled={supportSubmitting}
                  >
                    {supportSubmitting ? 'Sending...' : 'Send Support Request'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
